import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getStoreUniformEmployeeHistory,
  reapplyStoreUniformForm,
  submitStoreUniformForm,
} from '../../../services/storeUniformFormApi';
import { badge } from './StoreUniformFormList';

const getErrorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const StoreUniformEmployeeView = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [employee, setEmployee] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const loadEmployeeUniformDetails = useCallback(async () => {
    setEmployee(null);
    setRecords([]);
    setError('');
    setLoading(true);

    try {
      const response = await getStoreUniformEmployeeHistory(employeeId);
      setEmployee(response.data?.employee || null);
      setRecords(response.data?.forms || []);
    } catch (err) {
      setEmployee(null);
      setRecords([]);
      setError(getErrorMessage(err, 'Unable to load employee uniform details.'));
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    loadEmployeeUniformDetails();
  }, [loadEmployeeUniformDetails]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);

      navigate(location.pathname, {
        replace: true,
        state: {},
      });
    }
  }, [location, navigate]);

  const act = async (id, fn, msg) => {
    if (actionLoadingId || !window.confirm(msg)) return;
    setError('');
    setActionLoadingId(id);

    try {
      await fn();
      setSuccessMessage('Uniform request updated successfully.');
      await loadEmployeeUniformDetails();
    } catch (err) {
      setError(getErrorMessage(err, 'Unable to update the uniform request.'));
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="card p-4 text-center">
        <span className="spinner-border spinner-border-sm me-2" />
        Loading...
      </div>
    );
  }

  if (error && !employee) {
    return (
      <div className="store-page">
        <div className="alert alert-warning" role="alert">
          {error || 'This employee is not available for your outlet.'}
        </div>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => navigate('/store/uniform-forms')}
        >
          Back to Uniform Forms
        </button>
      </div>
    );
  }

  const e = employee || {};

  return (
    <div>
      <div className="d-flex justify-content-between mb-3">
        <h3>Employee Uniform History</h3>
        <Link className="btn btn-primary" to={`/store/uniform-forms/add/${employeeId}`}>Reapply Uniform</Link>
      </div>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setError('')} />
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setSuccessMessage('')} />
        </div>
      )}
      <div className="card p-3 mb-3">
        <h5>{e.employeeCode} - {e.name}</h5>
        <div>{e.designation} | {e.mobile}</div>
      </div>
      <div className="card table-responsive mb-3">
        <table className="table table-hover mb-0">
          <thead>
            <tr>
              <th>Form No</th>
              <th>Uniform Type</th>
              <th>Size</th>
              <th>Quantity</th>
              <th>Status</th>
              <th>Submitted At</th>
              <th>Provided At</th>
              <th>Admin Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.length ? records.map((f) => {
              const busy = actionLoadingId === f.id;
              return (
                <tr key={f.id}>
                  <td>{f.formNo}</td>
                  <td>{f.uniformType}</td>
                  <td>{f.size}</td>
                  <td>{f.quantity}</td>
                  <td><span className={`badge bg-${badge(f.status)}`}>{f.status}</span></td>
                  <td>{f.submittedAt ? new Date(f.submittedAt).toLocaleString() : '-'}</td>
                  <td>{(f.providedAt || f.approvedAt) ? new Date(f.providedAt || f.approvedAt).toLocaleString() : '-'}</td>
                  <td>{f.adminRemarks || '-'}</td>
                  <td>
                    <Link className="btn btn-sm btn-outline-info me-1" to={`/store/uniform-forms/view/${f.id}`}>View details</Link>
                    {['draft', 'reedit_enabled'].includes(f.status) && <Link className="btn btn-sm btn-outline-primary me-1" to={`/store/uniform-forms/edit/${f.id}`}>Edit</Link>}
                    {f.status === 'draft' && <button type="button" disabled={!!actionLoadingId} className="btn btn-sm btn-outline-success me-1" onClick={() => act(f.id, () => submitStoreUniformForm(f.id), 'Submit request?')}>{busy ? 'Submitting...' : 'Submit'}</button>}
                    {f.status === 'reedit_enabled' && <button type="button" disabled={!!actionLoadingId} className="btn btn-sm btn-outline-success me-1" onClick={() => act(f.id, () => submitStoreUniformForm(f.id), 'Resubmit request?')}>{busy ? 'Submitting...' : 'Resubmit'}</button>}
                    {['provided', 'approved', 'rejected'].includes(f.status) && <button type="button" disabled={!!actionLoadingId} className="btn btn-sm btn-outline-warning" onClick={() => act(f.id, () => reapplyStoreUniformForm(f.id), 'Create new draft from this request?')}>{busy ? 'Loading...' : 'Reapply Uniform'}</button>}
                    {f.status === 'submitted' && <span>Waiting for provided</span>}
                  </td>
                </tr>
              );
            }) : <tr><td colSpan="9" className="text-center py-4">No uniform forms found.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="card p-3">
        <h5>Timeline / Logs</h5>
        {records.flatMap((f) => (f.logs || []).map((l) => (
          <div key={`${f.id}-${l.id}`} className="border-start ps-3 mb-2">
            <b>{l.action}</b> ({f.formNo})
            <div className="text-muted">{new Date(l.createdAt).toLocaleString()} | {l.actorType}{l.remarks ? ` | ${l.remarks}` : ''}</div>
          </div>
        )))}
      </div>
    </div>
  );
};

export default StoreUniformEmployeeView;
